<template>
    <div class="paper-source">
        <b-alert
            show
            variant="info">
            {{ $t('source.fields_with_an_asterisk_are_mandatory_according_to_the_apa_guidelines') }}
        </b-alert>

        <b-form
            v-if="show"
            @submit="onSubmit"
            @reset="onReset">
            <div class="paper-type">
                <b-form-group label="Stacked radios">
                    <template #label>
                        <div class="header">
                            <span class="label">Type bron:<span class="required">*</span></span>
                            <span
                                class="alert text-danger"
                                v-if="errors.source_options_type"
                                role="alert">
                                <i class="fa fa-exclamation-circle"></i>
                                {{ $t('common.please_note') }}: {{ errors.source_options_type[0] }}
                            </span>
                        </div>
                    </template>
                    <b-form-radio-group
                        class="book-type-choice apprentice-radiobutton"
                        v-model="form.source_options_type"
                        stacked
                        type="text"
                        :options="source.options" />
                </b-form-group>
            </div>

            <div class="title">
                <b-form-group
                    :label="$t('source.article_title')"
                    label-for="paper-source-article-title">
                    <template #label>
                        <div class="header">
                            <span class="label">{{ $t('source.article_title') }}<span class="required">*</span></span>
                            <span
                                class="alert text-danger"
                                v-if="errors.article_title"
                                role="alert">
                                <i class="fa fa-exclamation-circle"></i>
                                {{ $t('common.please_note') }}: {{ errors.article_title[0] }}
                            </span>
                        </div>
                    </template>
                    <b-form-input
                        id="paper-source-article-title"
                        v-model="form.article_title"
                        type="text"
                        :placeholder="$t('source.article_title')"
                        :class="errors.article_title && 'is-invalid'" />
                </b-form-group>

                <b-form-group
                    :label="$t('source.name_paper_magazine')"
                    label-for="paper-source-paper-name">
                    <template #label>
                        <div class="header">
                            <span class="label"
                                >{{ $t('source.name_paper_magazine') }}<span class="required">*</span></span
                            >
                            <span
                                class="alert text-danger"
                                v-if="errors.paper_name"
                                role="alert">
                                <i class="fa fa-exclamation-circle"></i>
                                {{ $t('common.please_note') }}: {{ errors.paper_name[0] }}
                            </span>
                        </div>
                    </template>
                    <b-form-input
                        id="paper-source-paper-name"
                        v-model="form.paper_name"
                        type="text"
                        :placeholder="$t('source.article_title')"
                        :class="errors.paper_name && 'is-invalid'" />
                </b-form-group>
            </div>

            <div class="authors">
                <div class="label">
                    {{ $t('source.authors') }}:<span class="required">*</span>
                    <span
                        class="alert text-danger"
                        v-if="errors.authors"
                        role="alert">
                        <i class="fa fa-exclamation-circle"></i>
                        {{ $t('common.please_note') }}: {{ errors.authors[0] }}
                    </span>
                    <div class="add-author">
                        <b-button
                            class="btn-add btn-add-author"
                            variant="primary"
                            @click="addAuthor">
                            {{ $t('source.add_author') }}
                        </b-button>
                    </div>
                </div>

                <div
                    class="author"
                    v-if="!form.author_not_available"
                    v-for="(author, index) in form.authors"
                    :key="author.id">
                    <b-form-checkbox
                        class="apprentice-checkbox organisation-check"
                        v-model="form.organisation_check_box[index]">
                        <span>{{ $t('common.organization') }}</span>
                    </b-form-checkbox>

                    <div
                        class="initials"
                        v-if="!form.organisation_check_box[index]">
                        <b-form-input
                            v-model="form.authors[index].author_firstname"
                            type="text"
                            :placeholder="$t('source.initials')" />
                    </div>

                    <div
                        class="insertion"
                        v-if="!form.organisation_check_box[index]">
                        <b-form-input
                            v-model="form.authors[index].author_preposition"
                            type="text"
                            :placeholder="$t('common.infix')" />
                    </div>

                    <div
                        class="surname"
                        v-if="!form.organisation_check_box[index]">
                        <b-form-input
                            v-model="form.authors[index].author_lastname"
                            type="text"
                            :placeholder="$t('common.last_name')" />
                    </div>

                    <div
                        class="organisation"
                        v-if="form.organisation_check_box[index]">
                        <b-form-input
                            v-model="form.authors[index].organisation"
                            type="text"
                            :placeholder="$t('common.organization')" />
                    </div>

                    <div class="remove-author">
                        <b-button
                            class="btn-remove btn-remove-author d-none d-md-flex remove-icon-margin"
                            variant="danger-icon"
                            @click="removeAuthor(index)">
                        </b-button>
                        <b-button
                            class="btn-remove-author d-flex d-md-none"
                            variant="danger"
                            @click="removeAuthor(index)">
                            <i class="btn-remove"></i>{{ $t('source.delete_author') }}
                        </b-button>
                    </div>
                </div>
            </div>

            <div class="publication-date">
                <b-form-group
                    :label="$t('source.publication_date')"
                    label-for="internet-source-publication-date"
                    :description="$t('source.leave_blank_for_without_date')">
                    <b-form-datepicker
                        class="apprentice-date-picker btn-date-group"
                        id="paper-source-publication-date"
                        v-model="form.publication_date"
                        start-weekday="1"
                        now-button
                        reset-button
                        right
                        today-variant="primary"
                        nav-button-variant="primary"
                        locale="nl"
                        :aria-controls="$t('source.choose_a_date')"
                        label-reset-button="reset"
                        :label-no-date-selected="$t('source.choose_a_date')"
                        menu-class="datedropdown"
                        :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'short' }">
                    </b-form-datepicker>
                </b-form-group>
            </div>

            <div class="pages">
                <b-form-group
                    :label="$t('source.pages_of')"
                    label-for="paper-source-paper-name">
                    <template #label>
                        <div class="header">
                            <span class="label">{{ $t('source.pages_of') }}<span class="required">*</span></span>
                            <span
                                class="alert text-danger"
                                v-if="errors.page_start || errors.page_end"
                                role="alert">
                                <i class="fa fa-exclamation-circle"></i>
                                {{ $t('common.please_note') }}:
                                {{ errors.page_start ? errors.page_start : errors.page_end }}
                            </span>
                        </div>
                    </template>
                    <b-input-group
                        class="page-ranges-group"
                        prepend="Start">
                        <b-form-input
                            v-model="form.page_start"
                            :aria-label="$t('source.start_page')"
                            :placeholder="$t('source.start_page_example')"
                            type="text"
                            :class="errors.page_start && 'is-invalid'"></b-form-input>
                        <b-input-group-prepend is-text><span class="hyphen">-</span></b-input-group-prepend>
                        <b-input-group-prepend is-text
                            ><span>{{ $t('source.end') }}</span></b-input-group-prepend
                        >
                        <b-form-input
                            v-model="form.page_end"
                            :aria-label="$t('source.end_page')"
                            :placeholder="$t('source.end_page_example')"
                            type="text"
                            :class="errors.page_end && 'is-invalid'"></b-form-input>
                    </b-input-group>
                </b-form-group>
            </div>

            <div
                class="magazine"
                v-if="form.source_options_type == 4">
                <b-form-group
                    :label="$t('source.grade')"
                    label-for="paper-source-issue-number">
                    <template #label>
                        <div class="header">
                            <span class="label">{{ $t('source.grade') }}<span class="required">*</span></span>
                            <span
                                class="alert text-danger"
                                v-if="errors.issue_number"
                                role="alert">
                                <i class="fa fa-exclamation-circle"></i>
                                {{ $t('common.please_note') }}: {{ errors.issue_number }}
                            </span>
                        </div>
                    </template>
                    <b-form-input
                        id="paper-source-issue-number"
                        v-model="form.issue_number"
                        type="text"
                        :placeholder="$t('source.grade')"
                        :class="errors.issue_number && 'is-invalid'" />
                </b-form-group>

                <b-form-group
                    :label="$t('source.episode_number')"
                    label-for="paper-source-paper-name">
                    <template #label>
                        <div class="header">
                            <span class="label">{{ $t('source.episode_number') }}<span class="required">*</span></span>
                            <span
                                class="alert text-danger"
                                v-if="errors.episode_number"
                                role="alert">
                                <i class="fa fa-exclamation-circle"></i>
                                {{ $t('common.please_note') }}: {{ errors.episode_number }}
                            </span>
                        </div>
                    </template>
                    <b-form-input
                        id="paper-source-paper-name"
                        v-model="form.episode_number"
                        type="text"
                        :placeholder="$t('source.episode_number')"
                        :class="errors.episode_number && 'is-invalid'" />
                </b-form-group>
            </div>

            <div class="edition">
                <b-form-group label="Stacked radios">
                    <template #label>
                        <div class="header">
                            <span class="label">{{ $t('source.type_of_release') }}<span class="required">*</span></span>
                            <span
                                class="alert text-danger"
                                v-if="errors.publish_type"
                                role="alert">
                                <i class="fa fa-exclamation-circle"></i>
                                {{ $t('common.please_note') }}: {{ errors.publish_type }}
                            </span>
                        </div>
                    </template>
                    <b-form-radio-group
                        class="publish-type-choice apprentice-radiobutton"
                        v-model="form.publisher_type"
                        stacked
                        type="text"
                        :options="type.options" />
                </b-form-group>

                <div
                    class="printed"
                    v-if="form.publisher_type == 1">
                    <!--                    Todo: there is no option for printed why!-->
                </div>

                <div
                    class="website"
                    v-if="form.publisher_type == 3">
                    <b-form-group
                        label="Url:"
                        label-for="publisher-website-title">
                        <template #label>
                            <div class="header">
                                <span class="label">Url:</span>
                                <span
                                    class="alert text-danger"
                                    v-if="errors.publisher_url"
                                    role="alert">
                                    <i class="fa fa-exclamation-circle"></i>
                                    {{ $t('common.please_note') }}: {{ errors.publisher_url }}
                                </span>
                            </div>
                        </template>
                        <b-form-input
                            id="publisher-website-title"
                            v-model="form.publisher_url"
                            type="text"
                            placeholder="http://www.google.nl"
                            :class="errors.publisher_url && 'is-invalid'" />
                    </b-form-group>
                </div>
            </div>

            <div class="quote-source">
                <b-form-checkbox
                    class="apprentice-checkbox"
                    v-model="form.quote"
                    name="paper-quote-checkbox"
                    value="accepted">
                    <span>{{ $t('source.did_you_use_the_source_for_a_citation') }}</span>
                </b-form-checkbox>

                <div
                    class="quote"
                    v-if="form.quote">
                    <div class="muted-text description">
                        {{ $t('source.if_you_use_a_quotation_please_include_it_in_the_text_indicate_the_page_number') }}
                    </div>
                    <b-form-group
                        :label="$t('source.page_start_quotation')"
                        label-for="edition-source-title">
                        <template #label>
                            <div class="header">
                                <span class="label"
                                    >{{ $t('source.page_start_quotation') }}<span class="required">*</span></span
                                >
                                <span
                                    class="alert text-danger"
                                    v-if="errors.quote_page_end || errors.quote_page_start"
                                    role="alert">
                                    <i class="fa fa-exclamation-circle"></i>
                                    {{ $t('common.please_note') }}:
                                    {{
                                        errors.quote_page_start ? errors.quote_page_start[0] : errors.quote_page_end[0]
                                    }}
                                </span>
                            </div>
                        </template>
                        <b-input-group
                            class="quote-range"
                            prepend="Start">
                            <b-form-input
                                v-model="form.quote_page_start"
                                :aria-label="$t('source.start_quotation')"
                                :placeholder="$t('source.start_page_example')"></b-form-input>
                            <b-input-group-prepend is-text><span class="hyphen">-</span></b-input-group-prepend>
                            <b-input-group-prepend is-text
                                ><span>{{ $t('source.end') }}</span></b-input-group-prepend
                            >
                            <b-form-input
                                v-model="form.quote_page_end"
                                :aria-label="$t('source.end_quotation')"
                                type="text"
                                :placeholder="$t('source.end_page_example')"></b-form-input>
                        </b-input-group>
                    </b-form-group>
                </div>
            </div>

            <div class="paper-source-button-groups">
                <b-button
                    class="btn-save btn-source-book-save"
                    v-if="form.id == ''"
                    type="submit"
                    variant="primary">
                    {{ $t('source.save_source') }}
                </b-button>

                <b-button
                    class="btn-edit btn-source-book-edit"
                    v-else
                    type="submit"
                    variant="primary">
                    {{ $t('source.edit_source') }}
                </b-button>

                <b-button
                    class="btn-reset btn-source-book-reset-form"
                    type="reset"
                    variant="outline-primary">
                    {{ $t('source.clear_form') }}
                </b-button>
            </div>
        </b-form>
    </div>
</template>

<script>
import { SourceBus } from '../../app';

export default {
    name: 'design-source-paper-form',
    props: ['projectId'],
    data() {
        return {
            form: {
                base: 'PaperSource',
                type: '',
                id: '',
                source_options_type: 3,
                publisher_type: '',
                publisher_url: '',
                article_title: '',
                publication_date: '',
                page_start: '',
                page_end: '',
                paper_name: '',
                issue_number: '',
                episode_number: '',
                quote_page_start: '',
                quote_page_end: '',
                authors: [],
                organisation_check_box: [],
                quote: false,
                checked: [],
            },
            type: {
                options: [
                    { text: this.$t('source.printed'), value: 1 },
                    { text: 'Website', value: 3 },
                ],
            },

            source: {
                options: [
                    {
                        text: this.$t('source.newspaper_article'),
                        value: 3,
                    },
                    { text: this.$t('source.magazine_article'), value: 4 },
                ],
            },

            show: true,
            errors: [],
        };
    },
    methods: {
        onSubmit(evt) {
            evt.preventDefault();
            this.errors = [];

            let uri = '/source/store/' + this.projectId;
            let data = this.form;

            axios
                .post(uri, data)
                .then((response) => {
                    if (response.data) {
                        this.onReset(evt);

                        this.alert.success();

                        // Add source to overview.
                        SourceBus.$emit('pushData', response.data);
                    }
                })
                .catch((error) => {
                    if (error.response.status == 422) {
                        this.errors = error.response.data.errors;
                    }
                });
        },

        addAuthor() {
            this.form.authors.push({
                author_firstname: '',
                author_preposition: '',
                author_lastname: '',
                organisation: '',
            });
        },

        removeAuthor(index) {
            this.form.authors.splice(index, 1);
        },

        editField(data) {
            SourceBus.$emit('updateTabs', 'Paper');

            if (data.paper_article_title) {
                this.form.article_title = data.paper_article_title;
            }

            if (data.paper_year_volume) {
                this.form.episode_number = data.paper_year_volume;
            }

            if (data.paper_episode_number) {
                this.form.episode_number = data.paper_episode_number;
            }

            if (data.paper_name) {
                this.form.paper_name = data.paper_name;
            }

            if (data.paper_page_end) {
                this.form.page_end = data.paper_page_end;
            }

            if (data.paper_page_start) {
                this.form.page_start = data.paper_page_start;
            }

            if (data.paper_publication_date) {
                this.form.publication_date = data.paper_publication_date;
            }

            if (data.source_options_type) {
                this.form.source_options_type = data.source_options_type;
            }

            if (data.publish_type) {
                this.form.publisher_type = data.publish_type;
            }

            if (data.publish_url) {
                this.form.publisher_url = data.publish_url;
            }

            if (data.publish_url) {
                this.form.publisher_url = data.publish_url;
            }

            if (data.paper_year_volume) {
                this.form.issue_number = data.paper_year_volume;
            }

            if (data.id) {
                this.form.id = data.id;
            }

            if (data.authors) {
                data.authors.forEach((value, index) => {
                    this.form.authors = [];

                    // Default Author
                    if (!value.organisation) {
                        this.form.authors.push({
                            author_firstname: value.initials,
                            author_preposition: value.preposition,
                            author_lastname: value.last_name,
                            organisation: value.organisation,
                        });
                    } else {
                        this.form.organisation_check_box[index] = true;

                        this.form.authors.push({
                            author_firstname: value.initials,
                            author_preposition: value.preposition,
                            author_lastname: value.last_name,
                            organisation: value.organisation,
                        });
                    }
                });
            }
        },

        onReset(evt) {
            evt.preventDefault();
            /* Reset our form values */
            this.form.source = '';
            this.form.article_title = '';
            this.form.paper_name = '';
            this.form.publication_date = '';
            this.form.page_start = '';
            this.form.page_end = '';
            this.form.issue_number = '';
            this.form.episode_number = '';
            this.form.publisher_type = '';
            this.form.publisher_url = '';
            this.form.quote_page_start = '';
            this.form.quote_page_end = '';
            this.form.quote = false;
            this.form.id = '';

            /* Trick to reset/clear native browser form validation state */
            this.show = false;
            this.$nextTick(() => {
                this.show = true;
            });
        },
    },

    created() {
        SourceBus.$on('editPaperSource', (data) => {
            this.editField(data);
        });
    },
};
</script>

<style scoped lang="scss">
.form-group {
    .header {
        flex-direction: column;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: space-between;

        .alert {
            padding: 0;
            margin-bottom: 0;
        }
    }
}

.paper-type ::v-deep {
    legend + div {
        margin-left: 80px;
    }
}

.title,
.magazine {
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    .form-control {
        margin-bottom: 0;
    }
}

.authors {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

    .label {
        display: flex;
        flex: 1;
        flex-wrap: wrap;
        font-weight: $font-weight-medium;
        margin-bottom: map-get($grid-gutter-spacing, 'ten');

        .alert {
            padding: 0;
            margin-bottom: 0;
            margin-left: auto;
        }

        .add-author {
            display: flex;
            flex: 1 0 100%;
            justify-content: flex-end;
        }
    }

    .organisation {
        margin-bottom: map-get($grid-gutter-spacing, 'ten');
    }

    .author {
        width: 100%;
        border-bottom: 1px solid $border-color;
        padding-bottom: map-get($grid-gutter-spacing, 'ten');

        > div:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'eight');
        }

        .form-control {
            margin-bottom: 0;
        }

        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }

        .remove-author {
            display: flex;
            justify-content: flex-end;

            .btn-remove-author {
                display: flex;
                flex-direction: row;
                align-items: center;

                i {
                    margin-right: map-get($grid-gutter-spacing, 'eight');
                }
            }
        }
    }
}

.edition ::v-deep {
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

    legend + div {
        margin-left: 102px;
    }

    .publish-type-choice {
        max-width: fit-content;
    }
}

.printed {
    display: block;
}

.quote {
    .description {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }
}

.pages {
    margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

    .label {
        font-weight: $font-weight-bold;
        font-size: $default-font-size;
        line-height: 1;
        margin-bottom: map-get($grid-gutter-spacing, 'eight');
    }
}

.pages,
.quote-range {
    input {
        width: calc(100% - 60px);
    }

    :nth-child(3) {
        display: none;
    }

    :nth-child(4) {
        margin-top: map-get($grid-gutter-spacing, 'ten');

        .input-group-text {
            border-top-left-radius: $border-radius-content;
            border-bottom-left-radius: $border-radius-content;
        }
    }

    .form-control:nth-child(5) {
        margin-top: map-get($grid-gutter-spacing, 'ten');
    }
}

.paper-source-button-groups {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    margin-top: $grid-gutter-width;
    margin-bottom: $grid-gutter-width;

    button {
        &:not(:last-child) {
            margin-bottom: map-get($grid-gutter-spacing, 'ten');
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .form-group {
        .header {
            flex-direction: row;
            align-items: center;
        }
    }

    .title,
    .magazine {
        flex-direction: row;

        .form-group {
            flex: 1 1 calc(50% - 15px);

            &:first-child {
                margin-right: 30px;
            }
        }
    }

    .authors {
        .label {
            align-items: center;
            flex-wrap: nowrap;

            .add-author {
                flex: initial;
                justify-content: initial;
                margin-left: auto;
            }
        }

        .author-unknown {
            flex-direction: row;
            align-items: center;

            .author-unknown-check {
                margin-bottom: 0;
            }
        }

        .author {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: baseline;

            .organisation-check {
                flex: 1 1 100%;
            }

            .initials,
            .surname {
                min-width: 165px;
            }

            .insertion {
                width: 125px;
            }

            .form-control {
                margin-bottom: 0;
            }
        }
    }

    .pages,
    .quote-range {
        input {
            width: auto;
        }

        :nth-child(3) {
            display: flex;

            .input-group-text {
                background: $white;
                color: $body-color;
                border: none;
            }
        }

        :nth-child(4),
        .form-control:nth-child(5) {
            margin-top: 0;
        }
    }

    .printed {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: baseline;

        div {
            flex: 1;

            &:not(:last-child) {
                margin-right: $grid-gutter-width;
            }
        }
    }

    .paper-source-button-groups {
        flex-direction: row;

        button {
            &:not(:last-child) {
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
                margin-bottom: 0;
            }
        }
    }
}

@media (min-width: map-get($grid-breakpoints, 'xl')) {
    form {
        max-width: 55%;
    }

    .authors {
        .author {
            .initials,
            .surname {
                flex: 1;
            }

            .surname {
                margin-right: map-get($grid-gutter-spacing, 'fifteen');
            }

            .insertion {
                margin-right: $grid-gutter-width;
                margin-left: $grid-gutter-width;
            }
        }
    }
}
</style>
